import React, { useEffect, useRef, useState } from 'react';
import { Navigation } from '../../global/models/navigation-model';
import { useDeserializedToken } from '../../pages/arc/components/use-deserialized-token';
import { isReloadPending } from './prime-and-reload';

const FTL_PARAM = 'ftl';
const FTL_VALUE = 'ftl';
const DISMISS_MS = 3000;

interface Props {
	navigation: Navigation,
}

const isFirstTimeLogin = (): boolean => {
	if (typeof window === 'undefined') {
		return false;
	}
	const params = new URLSearchParams(window.location.search);
	return params.get(FTL_PARAM) === FTL_VALUE;
};

const stripFtlParam = () => {
	if (typeof window === 'undefined' || !window.history?.replaceState) {
		return;
	}
	const url = new URL(window.location.href);
	url.searchParams.delete(FTL_PARAM);
	window.history.replaceState(window.history.state, '', url.toString());
};

export const SignedInToast = (props: Props) => {
	const token = useDeserializedToken();
	const [visible, setVisible] = useState(false);
	const hasFired = useRef(false);
	const personGuid = token?.person?.guid;

	useEffect(() => {
		// Defer to the prime-and-reload flow: when a reload is pending (?src=si and the server
		// hasn't established the session yet), don't fire or strip ftl here — the page is about
		// to reload with the primed cookie, and ftl is preserved across that reload so the toast
		// shows on the reloaded (authenticated) page. If the server already rendered
		// authenticated, no reload happens, so we fire here as usual.
		if (hasFired.current || !isFirstTimeLogin() || !personGuid || isReloadPending()) {
			return undefined;
		}

		hasFired.current = true;
		stripFtlParam();
		setVisible(true);

		const timer = window.setTimeout(() => setVisible(false), DISMISS_MS);
		return () => window.clearTimeout(timer);
	}, [personGuid]);

	if (!visible || !token?.person) {
		return null;
	}

	const { fn, ln } = token.person;
	const fullName = [fn, ln].filter(Boolean).join(' ');
	const template = props.navigation?.SignedInAsText || 'Signed in as';
	const text = template.includes('{0}')
		? template.replace('{0}', fullName)
		: `${template} ${fullName}`;

	return (
		<div className='signed-in-toast' role='status' aria-live='polite'>
			<svg className='signed-in-toast-icon' aria-hidden='true' focusable='false'>
				<use xmlnsXlink='http://www.w3.org/1999/xlink' xlinkHref='#check-icon' />
			</svg>
			<span className='signed-in-toast-text'>{text}</span>
		</div>
	);
};
